import { useState } from "react";
import { Box, Group, Paper, Stack, Text } from "@mantine/core";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import corridorCardClasses from "@/components/ecosystem-head/EcosystemHeadCorridorCard.module.css";
import {
  ECOSYSTEM_CORRIDOR_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import { comingSoonCorridorHasFlags } from "@/shared/constants/comingSoonEcosystemCorridors.const";

const CARD_NAVY = "#04305c";
const CARD_BLUE = "#2563EB";

type EcosystemCorridorPlaceholderCardProps = {
  corridor: ComingSoonEcosystemCorridor;
  /** Fixed width for horizontal scroll layouts (e.g. explore page). */
  fixedWidth?: number;
  fullWidth?: boolean;
  /** Use compact flag badges (sector carousel) vs inline flags (explore grid). */
  variant?: "carousel" | "explore";
};

function PlaceholderHeaderFlags({ corridor }: { corridor: ComingSoonEcosystemCorridor }) {
  const originCountry = corridor.country?.trim() || null;
  const targetCountry = corridor.targetCountry?.trim() || null;
  const originLabel = corridor.originLabel.trim();
  const targetLabel = corridor.targetLabel.trim();

  const showFlags =
    comingSoonCorridorHasFlags(corridor) &&
    Boolean(originCountry && countryHasRenderableFlag(originCountry)) &&
    Boolean(targetCountry && countryHasRenderableFlag(targetCountry));

  if (showFlags && originCountry && targetCountry) {
    return (
      <Box className={corridorCardClasses.flagPair}>
        <CountryFlag country={originCountry} />
        <CorridorDoubleArrow size={12} color={CARD_BLUE} />
        <CountryFlag country={targetCountry} />
      </Box>
    );
  }

  // Regions / non-ISO endpoints: stacked labels in the flag column.
  return (
    <Box className={corridorCardClasses.flagPair}>
      <Text size="xs" fw={700} c={CARD_NAVY} ta="center" style={{ lineHeight: 1.2, maxWidth: 56 }}>
        {originLabel}
      </Text>
      <CorridorDoubleArrow size={12} color={CARD_BLUE} />
      <Text size="xs" fw={700} c={CARD_NAVY} ta="center" style={{ lineHeight: 1.2, maxWidth: 56 }}>
        {targetLabel}
      </Text>
    </Box>
  );
}

export default function EcosystemCorridorPlaceholderCard({
  corridor,
  fixedWidth,
  fullWidth = false,
  variant = "carousel",
}: EcosystemCorridorPlaceholderCardProps) {
  const isUnderway = corridor.status === "underway";
  const statusLabel = isUnderway ? "Underway" : "Coming Soon";
  const isExplore = variant === "explore";
  const cardHeight = isExplore ? ECOSYSTEM_EXPLORE_CARD_HEIGHT : ECOSYSTEM_CORRIDOR_CARD_HEIGHT;
  const cardWidth =
    fullWidth && isExplore ? undefined : isExplore ? ECOSYSTEM_EXPLORE_CARD_WIDTH : fixedWidth;
  const [isHovered, setIsHovered] = useState(false);
  const corridorTitle = corridor.corridorSubtitle.trim();
  const countryText = `${corridor.originLabel.trim()} ↔ ${corridor.targetLabel.trim()}`;

  return (
    <Paper
      radius="lg"
      shadow="none"
      h={fullWidth ? "100%" : cardHeight}
      className={corridorCardClasses.card}
      onMouseEnter={isExplore ? () => setIsHovered(true) : undefined}
      onMouseLeave={isExplore ? () => setIsHovered(false) : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${isExplore && isHovered ? "#93C5FD" : "#E2E8F0"}`,
        overflow: "hidden",
        boxShadow: isExplore
          ? isHovered
            ? "0 12px 28px rgba(4, 48, 92, 0.14)"
            : "0 4px 14px rgba(15, 23, 42, 0.06)"
          : "0 4px 14px rgba(15, 23, 42, 0.06)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        minHeight: fullWidth ? 0 : cardHeight,
        ...(cardWidth
          ? { flex: "0 0 auto", width: cardWidth, maxWidth: "85vw" }
          : fullWidth && isExplore
            ? { width: "100%", flex: 1 }
            : undefined),
      }}
    >
      <Box className={corridorCardClasses.cardContent}>
        <Group
          wrap="nowrap"
          align="stretch"
          gap={10}
          pl={0}
          pr={12}
          py={10}
          style={{ flex: 1, minHeight: 0 }}
        >
          <Box className={corridorCardClasses.flagContainer}>
            <PlaceholderHeaderFlags corridor={corridor} />
          </Box>

          <Stack gap={4} justify="center" style={{ flex: 1, minWidth: 0 }}>
            <Text
              fw={800}
              fz={fullWidth || isExplore ? "1rem" : 14}
              c={CARD_NAVY}
              lineClamp={3}
              style={{
                lineHeight: 1.25,
                letterSpacing: "0.01em",
                overflowWrap: "normal",
                wordBreak: "normal",
                hyphens: "none",
              }}
            >
              {corridorTitle}
            </Text>
            <Text fz={12} c="#64748B" fw={600} style={{ lineHeight: 1.25 }}>
              {countryText}
            </Text>
          </Stack>

          <Stack
            gap={6}
            justify="space-between"
            align="flex-end"
            style={{ width: fullWidth ? 110 : 100, flexShrink: 0 }}
          >
            <Box style={{ width: "100%", marginTop: 4 }} />
            <Box w="100%">
              <Box className={corridorCardClasses.ctaComingSoon}>
                {statusLabel}
              </Box>
            </Box>
          </Stack>
        </Group>
      </Box>
    </Paper>
  );
}
